import { api } from './apiClient';

// Indian States with GST codes
export const INDIAN_STATES = [
  { code: '01', name: 'Jammu & Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '26', name: 'Dadra & Nagar Haveli and Daman & Diu' },
  { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman & Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
  { code: '38', name: 'Ladakh' },
];

export interface Customer {
  id: number;
  customer_code: string;
  company_name: string;
  contact_person: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  gstin?: string;
  credit_limit: number;
  credit_days: number;
  is_active: boolean;
  created_at: string;
  outstanding_balance?: number;
}

export interface CreateCustomerRequest {
  company_name: string;
  contact_person: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  gstin?: string;
  credit_limit?: number;
  credit_days?: number;
}

export interface CustomerFilters {
  search?: string;
  state?: string;
  is_active?: boolean;
}

// Validate GSTIN format
export const validateGSTIN = (gstin: string): { valid: boolean; error?: string } => {
  if (!gstin) return { valid: true };

  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  if (gstin.length !== 15) {
    return { valid: false, error: 'GSTIN must be 15 characters' };
  }

  if (!gstinRegex.test(gstin)) {
    return { valid: false, error: 'Invalid GSTIN format' };
  }

  // Validate state code
  const stateCode = gstin.substring(0, 2);
  const validState = INDIAN_STATES.find(s => s.code === stateCode);
  if (!validState) {
    return { valid: false, error: 'Invalid state code in GSTIN' };
  }

  return { valid: true };
};

// Extract state from GSTIN
export const getStateFromGSTIN = (gstin: string): string | null => {
  if (!gstin || gstin.length < 2) return null;
  const stateCode = gstin.substring(0, 2);
  const state = INDIAN_STATES.find(s => s.code === stateCode);
  return state?.name || null;
};

export const customerService = {
  // Get all customers
  getCustomers: async (filters?: CustomerFilters): Promise<Customer[]> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.state) params.append('state', filters.state);
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));

    const queryString = params.toString();
    const url = `/sales/customers${queryString ? `?${queryString}` : ''}`;
    const response = await api.get<{ customers: Customer[]; count: number }>(url);
    return response.customers || [];
  },

  // Get customer by ID
  // NOTE: unused anywhere in the app today, but this GET also nests under a
  // `customer` key (see src/controllers/sales.js getCustomerById) — kept
  // flat here on purpose is wrong; left as-is per audit scope (no live
  // caller to break), see apiClient.ts audit notes.
  getCustomerById: async (id: number): Promise<Customer> => {
    return api.get<Customer>(`/sales/customers/${id}`);
  },

  // Create new customer
  createCustomer: async (data: CreateCustomerRequest): Promise<Customer> => {
    // Transform snake_case to camelCase for backend
    const payload = {
      companyName: data.company_name,
      contactPerson: data.contact_person,
      email: data.email,
      phone: data.phone,
      address: data.address,
      city: data.city,
      state: data.state,
      postalCode: data.postal_code,
      gstin: data.gstin,
      creditLimit: data.credit_limit,
      creditDays: data.credit_days,
    };
    // Controller nests the result under a `customer` key (see
    // src/controllers/sales.js createCustomer) — Dashboard.tsx reads
    // `customer.company_name` off this return value.
    //
    // That nested object is ALSO camelCase, not snake_case like every other
    // customer-returning endpoint (get/list/update all do `SELECT *` and come
    // back snake_case) — Sales.createCustomer in the API hand-builds its
    // return value from the insert params instead of re-querying the row.
    // Verified live: POST /sales/customers returns
    // `data:{customer:{id,customerCode,companyName,contactPerson,email,phone,
    // gstin,creditLimit,creditDays,gstDetails}}` — no address/city/state/
    // postalCode/isActive/createdAt at all. Normalize to the canonical
    // snake_case `Customer` shape here so callers don't have to know about
    // this one endpoint's inconsistency; the address-family fields and
    // is_active/created_at aren't returned so they're filled from the
    // request / a reasonable default rather than invented data.
    const response = await api.post<{
      customer: {
        id: number;
        customerCode: string;
        companyName: string;
        contactPerson: string;
        email: string | null;
        phone: string | null;
        gstin: string | null;
        creditLimit: number;
        creditDays: number;
      };
    }>('/sales/customers', payload);
    const c = response.customer;
    return {
      id: c.id,
      customer_code: c.customerCode,
      company_name: c.companyName,
      contact_person: c.contactPerson,
      email: c.email ?? undefined,
      phone: c.phone ?? undefined,
      address: data.address,
      city: data.city,
      state: data.state,
      postal_code: data.postal_code,
      gstin: c.gstin ?? undefined,
      credit_limit: c.creditLimit,
      credit_days: c.creditDays,
      is_active: true,
      created_at: new Date().toISOString(),
    };
  },

  // Update customer
  updateCustomer: async (id: number, data: Partial<CreateCustomerRequest>): Promise<Customer> => {
    // Controller nests the payload under a `customer` key (see
    // src/controllers/sales.js updateCustomer) — apiClient only unwraps the
    // outer `{status, data}` envelope, not this inner key.
    const response = await api.put<{ customer: Customer }>(`/sales/customers/${id}`, data);
    return response.customer;
  },

  // Search customers (for autocomplete)
  searchCustomers: async (query: string): Promise<Customer[]> => {
    const response = await api.get<{ customers: Customer[]; count: number }>(`/sales/customers?search=${encodeURIComponent(query)}`);
    return response.customers || [];
  },
};

export default customerService;
